/**
 * How long stock has been sitting, and whether that's become a problem. Fresh
 * meat keeps for days in a fridge and months in a freezer, so the question is
 * different depending on where a package is: in a fridge, "should this go in
 * the freezer before it spoils?"; in a freezer, "has this been here too long?"
 *
 * Shared by the API (the Stock page's alert counts) and the web app (each
 * package's badge), so both flag the same packages.
 */

/** Days fresh meat keeps out of the freezer, when its SKU doesn't say. */
export const DEFAULT_FRIDGE_LIFE_DAYS = 7;
/** Days meat keeps in the freezer, when its SKU doesn't say. */
export const DEFAULT_FREEZER_LIFE_DAYS = 365;
/** How many days before its fridge life runs out a package is flagged. */
export const FREEZE_SOON_DAYS = 2;

/** SKU kinds that are meat, and so get the defaults above. */
const PERISHABLE_KINDS = new Set(['raw_product', 'finished_product']);

export type StockAgingStatus =
  /** In a fridge and about to run out of fridge life. */
  | 'freeze_soon'
  /** In a fridge past its fridge life — move it to the freezer (or sell it). */
  | 'move_to_freezer'
  /** In a freezer past its freezer life — use it or sell it off. */
  | 'past_freezer_life'
  | 'ok';

export interface StockAgingInput {
  /** When the package was made or received. */
  since: Date | string | null | undefined;
  /** Whether it's in (or in a box in) a freezer location. */
  inFreezer: boolean;
  /** The SKU's kind; only meat gets default lives. */
  skuKind?: string | null;
  fridgeLifeDays?: number | null;
  freezerLifeDays?: number | null;
  /** Defaults to now; injectable for tests. */
  now?: Date;
}

/**
 * Whole days since a date.
 * @param since - The start
 * @param now - The end (defaults to now)
 * @returns Days, or null when there's no date
 */
export function daysSince(
  since: Date | string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!since) return null;
  const t = new Date(since).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now.getTime() - t) / 86_400_000));
}

/**
 * Where one package stands on age. A SKU that isn't meat and sets no lives of
 * its own is never flagged — honey doesn't need freezing.
 * @param input - The package's age, place and its SKU's lives
 * @returns The status, with its age in days
 */
export function stockAgingStatus(input: StockAgingInput): {
  status: StockAgingStatus;
  ageDays: number | null;
  /** The life it was measured against (fridge or freezer), when it has one. */
  lifeDays: number | null;
} {
  const ageDays = daysSince(input.since, input.now);
  const perishable = PERISHABLE_KINDS.has(input.skuKind ?? '');
  const lifeDays = input.inFreezer
    ? (input.freezerLifeDays ?? (perishable ? DEFAULT_FREEZER_LIFE_DAYS : null))
    : (input.fridgeLifeDays ?? (perishable ? DEFAULT_FRIDGE_LIFE_DAYS : null));
  const result = (status: StockAgingStatus) => ({ status, ageDays, lifeDays });
  if (ageDays == null || lifeDays == null) return result('ok');

  if (input.inFreezer) {
    return result(ageDays > lifeDays ? 'past_freezer_life' : 'ok');
  }
  if (ageDays >= lifeDays) return result('move_to_freezer');
  if (ageDays >= lifeDays - FREEZE_SOON_DAYS) return result('freeze_soon');
  return result('ok');
}

/**
 * The locations that count as freezer: any flagged one, and everything nested
 * inside it (a freezer's shelves are cold too).
 * @param locations - A vendor's locations
 * @returns Ids of every freezer location
 */
export function freezerLocationIds(
  locations: readonly (
    | {
        _id: unknown;
        parent?: unknown;
        isFreezer?: boolean | null;
      }
    | null
    | undefined
  )[],
): Set<string> {
  const byId = new Map<
    string,
    { parent?: unknown; isFreezer?: boolean | null }
  >();
  for (const l of locations) if (l) byId.set(String(l._id), l);
  const cold = new Set<string>();
  for (const id of byId.keys()) {
    const seen = new Set<string>();
    let cur: string | undefined = id;
    while (cur && !seen.has(cur)) {
      seen.add(cur);
      const loc = byId.get(cur);
      if (!loc) break;
      if (loc.isFreezer) {
        cold.add(id);
        break;
      }
      cur = loc.parent != null ? String(loc.parent) : undefined;
    }
  }
  return cold;
}
